/**
 * DeleteRecordModal — type-to-confirm permanent deletion of a single medical
 * record. Reached from the record's rename ("edit") editor. The recruiter must
 * type the word DELETE to enable the button; there is no undo (the file + its
 * S3 object are removed, and any AI facts derived only from it go with it).
 */
import { useState, useEffect, useCallback } from 'react';
import { X, AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { Z } from '../ui';

const CONFIRM_WORD = 'DELETE';

interface Props {
  filename: string;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function DeleteRecordModal({ filename, isDeleting, onConfirm, onCancel }: Props) {
  const [text, setText] = useState('');
  const ok = text.trim() === CONFIRM_WORD;

  const onKey = useCallback(
    (e: KeyboardEvent) => { if (e.key === 'Escape' && !isDeleting) onCancel(); },
    [onCancel, isDeleting],
  );
  useEffect(() => {
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onKey]);

  return (
    <div className={`fixed inset-0 ${Z.modal} overflow-y-auto`}>
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={isDeleting ? undefined : onCancel}
        aria-hidden="true"
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-record-title"
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-red-100 p-2 dark:bg-red-900/30">
                <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
              </div>
              <h2 id="delete-record-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Delete this record
              </h2>
            </div>
            <button
              onClick={onCancel}
              disabled={isDeleting}
              className="rounded p-1 text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-200"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="space-y-4 px-6 py-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              You are about to permanently delete{' '}
              <span className="break-all font-semibold text-gray-900 dark:text-gray-100">{filename}</span>.
              The file is removed for good, along with any diagnoses or medications the AI
              found only in it.
            </p>
            <p className="text-sm font-medium text-red-600 dark:text-red-400">
              This cannot be undone.
            </p>
            <div>
              <label htmlFor="delete-record-input" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Type <span className="font-mono font-bold text-red-600 dark:text-red-400">{CONFIRM_WORD}</span> to confirm
              </label>
              <input
                id="delete-record-input"
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && ok && !isDeleting) onConfirm(); }}
                placeholder={CONFIRM_WORD}
                disabled={isDeleting}
                autoFocus
                autoComplete="off"
                spellCheck={false}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/40">
            <button
              onClick={onCancel}
              disabled={isDeleting}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={!ok || isDeleting}
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {isDeleting ? 'Deleting…' : 'Delete record'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
